import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Info, SendHorizonal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { useProduct } from "@/context/ProductContext";

type Msg = {
  id: string;
  direction: "inbound" | "outbound";
  content: string;
  delivery_status: string;
  segments: number;
  created_at: string;
  provider_message_id?: string;
};

export default function Simulator() {
  const { product_name } = useProduct();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [needsPhone, setNeedsPhone] = useState(false);
  const [jobs, setJobs] = useState<Record<string, number>>({});
  const [lastNormalized, setLastNormalized] = useState<any>(null);
  const [showInspector, setShowInspector] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const [msgs, phones, jobsData] = await Promise.all([
        api.get("/simulator/messages").then((r) => r.data),
        api.get("/phone/list").then((r) => r.data),
        api.get("/simulator/jobs").then((r) => r.data),
      ]);
      setMessages(msgs);
      setJobs(jobsData);
      setNeedsPhone(!phones.some((p: any) => p.verified));
    } catch (err) {
      toast.error(apiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const r = await api.post("/simulator/send", { text });
      setMessages((m) => [...m, r.data.inbound, r.data.outbound]);
      setLastNormalized(r.data.normalized);
      setJobs(r.data.jobs || {});
      setText("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  if (needsPhone) {
    return (
      <div className="rounded-3xl border border-border bg-card p-8 text-center" data-testid="simulator-needs-phone">
        <h2 className="font-display text-2xl font-semibold">Verify a phone first</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The simulator uses the same path a real text message would.
        </p>
        <Link to="/onboarding">
          <Button className="mt-6 rounded-full" data-testid="go-onboarding">Add & verify phone</Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="simulator-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Simulator</h1>
          <p className="text-sm text-muted-foreground">Text {product_name} — development tool.</p>
        </div>
        <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary" data-testid="simulated-badge">
          Simulated SMS
        </span>
      </div>

      <div className="rounded-3xl border border-border bg-card">
        <div className="max-h-[52vh] min-h-[280px] space-y-3 overflow-y-auto p-4" data-testid="message-list">
          {messages.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Send a message to start the conversation.
            </p>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.direction === "inbound" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`bubble-${m.direction}`}
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                  m.direction === "inbound"
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-secondary text-secondary-foreground rounded-bl-md"
                }`}
              >
                <p className="whitespace-pre-wrap">{m.content}</p>
                <div className={`mt-1 text-[10px] ${m.direction === "inbound" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  {" · "}{m.delivery_status}{" · "}{m.segments} seg
                </div>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="flex items-center gap-2 border-t border-border p-3">
          <Input value={text} onChange={(e) => setText(e.target.value)} data-testid="simulator-input"
            placeholder="Text Remy…" className="rounded-full" />
          <Button type="submit" size="icon" className="rounded-full" disabled={sending} data-testid="simulator-send">
            <SendHorizonal className="h-4 w-4" />
          </Button>
        </form>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        {["pending", "processing", "completed"].map((k) => (
          <div key={k} className="rounded-2xl border border-border bg-card p-3 text-center" data-testid={`job-count-${k}`}>
            <div className="text-lg font-semibold">{jobs[k] || 0}</div>
            <div className="text-[11px] capitalize text-muted-foreground">{k} jobs</div>
          </div>
        ))}
      </div>

      <button
        onClick={() => setShowInspector((s) => !s)}
        className="mt-4 flex w-full items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 text-sm"
        data-testid="inspector-toggle"
      >
        <span className="inline-flex items-center gap-2"><Info className="h-4 w-4" /> Last normalized inbound</span>
        {showInspector ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {showInspector && (
        <pre className="mt-2 overflow-x-auto rounded-2xl border border-border bg-secondary/40 p-4 text-xs" data-testid="normalized-json">
          {lastNormalized ? JSON.stringify(lastNormalized, null, 2) : "Send a message to inspect the normalized payload."}
        </pre>
      )}

      <p className="mt-4 text-center text-xs text-muted-foreground">
        See every event in <Link to="/activity" className="text-primary">Activity</Link>.
      </p>
    </div>
  );
}
